import React, { useState, useEffect } from 'react';
import {
  CreditCard, Search, Check, AlertCircle, Loader, X,
  Phone, Award, Plus, Minus, Gift, Star
} from 'lucide-react';
import {
  getLoyaltyCards, getLoyaltyCard, adjustLoyaltyPoints,
  getLoyaltyRewards, redeemLoyaltyReward
} from '../../services/api';
import '../Boutiques.css';

const TIER_STYLE = {
  bronze: { bg: '#fef3c7', color: '#92400e', label: 'Bronze' },
  silver: { bg: '#e2e8f0', color: '#475569', label: 'Argent' },
  gold:   { bg: '#fef9c3', color: '#a16207', label: 'Or' },
};
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

export default function LoyaltyCards() {
  const [cards,   setCards]   = useState([]);
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [toast,   setToast]   = useState({ type: '', text: '' });

  // Détail
  const [detail,  setDetail]  = useState(null); // { card+transactions }
  const [rewards, setRewards] = useState([]);
  const [busy,    setBusy]    = useState(false);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast({ type: '', text: '' }), 4000);
  };

  const load = async (q = '') => {
    setLoading(true);
    try {
      const { data } = await getLoyaltyCards(q ? { search: q } : undefined);
      setCards(data.data || []);
      setStats(data.stats || null);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur de chargement');
    } finally { setLoading(false); }
  };

  const openDetail = async (cardId) => {
    setBusy(true);
    try {
      const [{ data: d }, { data: r }] = await Promise.all([
        getLoyaltyCard(cardId),
        getLoyaltyRewards({ actifOnly: true }),
      ]);
      setDetail(d.data);
      setRewards(r.data || []);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur');
    } finally { setBusy(false); }
  };
  const closeDetail = () => { setDetail(null); setRewards([]); };
  const refreshDetail = async () => { if (detail) await openDetail(detail.id); await load(search); };

  const handleAdjust = async (sign) => {
    const raw = window.prompt(`Nombre de points à ${sign > 0 ? 'créditer' : 'retirer'} :`, '50');
    if (raw === null) return;
    const n = Math.abs(parseInt(raw, 10));
    if (!n) return;
    const reason = window.prompt('Motif (optionnel) :', sign > 0 ? 'Geste commercial' : 'Correction') || undefined;
    setBusy(true);
    try {
      await adjustLoyaltyPoints({ cardId: detail.id, delta: sign * n, reason });
      showToast('success', 'Solde ajusté');
      await refreshDetail();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur');
    } finally { setBusy(false); }
  };

  const handleRedeem = async (reward) => {
    if (!window.confirm(`Échanger ${reward.coutPoints} points contre « ${reward.nom} » ? Le client sera notifié par WhatsApp.`)) return;
    setBusy(true);
    try {
      const { data } = await redeemLoyaltyReward({ cardId: detail.id, rewardId: reward.id });
      const code = data?.data?.coupon?.code;
      showToast('success', code ? `Bon ${code} créé — client notifié` : 'Récompense validée — client notifié');
      await refreshDetail();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur');
    } finally { setBusy(false); }
  };

  if (loading) return (
    <div className="bt-loading"><Loader className="bt-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="bt-page">
      {toast.text && (
        <div className={`bt-toast bt-toast--${toast.type}`}>
          {toast.type === 'success' ? <Check size={14}/> : <AlertCircle size={14}/>}
          {toast.text}
        </div>
      )}

      <div className="bt-header">
        <div className="bt-header-left">
          <div className="bt-header-icon"><CreditCard size={20}/></div>
          <div>
            <h1 className="bt-header-title">Cartes de fidélité</h1>
            <p className="bt-header-sub">Cartes dématérialisées, soldes de points et paliers de vos clients</p>
          </div>
        </div>
      </div>

      <div className="bt-content">
        {/* KPI */}
        {stats && (
          <div className="bt-kpi-row">
            {[
              { label: 'Cartes', value: fmt(stats.cartes), color: '#6366f1', bg: '#eef2ff', icon: CreditCard },
              { label: 'Points en circulation', value: fmt(stats.soldeTotal), color: '#10b981', bg: '#ecfdf5', icon: Star },
              { label: 'Points gagnés (cumul)', value: fmt(stats.pointsGagnes), color: '#f59e0b', bg: '#fffbeb', icon: Award },
              { label: 'Points dépensés', value: fmt(stats.pointsDepenses), color: '#ef4444', bg: '#fef2f2', icon: Gift },
            ].map((k, i) => {
              const Icon = k.icon;
              return (
                <div key={i} className="bt-kpi" style={{ borderTopColor: k.color }}>
                  <div className="bt-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={16}/></div>
                  <div className="bt-kpi-val">{k.value}</div>
                  <div className="bt-kpi-label">{k.label}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Recherche */}
        <form onSubmit={(e) => { e.preventDefault(); load(search); }}
          style={{ display: 'flex', gap: 8, marginBottom: 12, maxWidth: 420 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }}/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou téléphone…"
              style={{ width: '100%', padding: '8px 10px 8px 30px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }}/>
          </div>
          <button type="submit" className="bt-cta-btn">Rechercher</button>
        </form>

        {cards.length === 0 ? (
          <div className="bt-empty">
            <div className="bt-empty-icon"><CreditCard size={30}/></div>
            <h3>Aucune carte de fidélité</h3>
            <p>Les cartes se créent automatiquement dès la première vente d'un client.</p>
          </div>
        ) : (
          <div className="bt-card">
            <table className="bt-table">
              <thead>
                <tr><th>Client</th><th>Palier</th><th>Solde</th><th>Cumul gagné</th><th>Dépensé</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {cards.map((c) => {
                  const ts = TIER_STYLE[c.tier] || TIER_STYLE.bronze;
                  return (
                    <tr key={c.id}>
                      <td>
                        <div className="bt-cell-name">{c.contact?.nom || '—'}</div>
                        {c.contact?.phone && <div className="bt-cell-sub"><Phone size={10}/> {c.contact.phone}</div>}
                      </td>
                      <td><span className="bt-badge" style={{ background: ts.bg, color: ts.color }}>{ts.label}</span></td>
                      <td><strong>{fmt(c.balance)}</strong> pts</td>
                      <td>{fmt(c.totalEarned)}</td>
                      <td>{fmt(c.totalSpent)}</td>
                      <td>
                        <button className="bt-action-btn bt-action-btn--edit" onClick={() => openDetail(c.id)}>
                          Gérer
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Détail carte */}
      {detail && (
        <>
          <div className="bt-overlay" onClick={closeDetail}/>
          <div className="bt-modal bt-modal--lg">
            <div className="bt-modal-head">
              <div>
                <h2 className="bt-modal-title"><CreditCard size={15}/> Carte de fidélité</h2>
                <p className="bt-modal-sub">
                  Solde : <strong>{fmt(detail.balance)} pts</strong> · Niveau {TIER_STYLE[detail.tier]?.label || detail.tier}
                </p>
              </div>
              <button className="bt-modal-close" onClick={closeDetail}><X size={16}/></button>
            </div>

            <div className="bt-modal-body">
              {/* Ajustement */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <button className="bt-order-btn bt-order-btn--valid" disabled={busy} onClick={() => handleAdjust(+1)}>
                  <Plus size={12}/> Créditer
                </button>
                <button className="bt-order-btn bt-order-btn--cancel" disabled={busy} onClick={() => handleAdjust(-1)}>
                  <Minus size={12}/> Retirer
                </button>
              </div>

              {/* Récompenses échangeables */}
              <div className="bt-section-label">Échanger contre une récompense</div>
              {rewards.length === 0 ? (
                <p className="bt-cell-sub" style={{ margin: '6px 0 14px' }}>Aucune récompense active. Créez-en dans l'onglet Récompenses.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, marginBottom: 16 }}>
                  {rewards.map(r => {
                    const affordable = detail.balance >= r.coutPoints;
                    return (
                      <div key={r.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>
                          {r.nom}
                          <span className="bt-badge" style={{ background: '#eef2ff', color: '#4338ca', marginLeft: 6 }}>
                            {r.type === 'percentage' ? `-${r.valeur}%` : `-${fmt(r.valeur)} XOF`}
                          </span>
                        </div>
                        {r.description && <div className="bt-cell-sub" style={{ marginTop: 2 }}>{r.description}</div>}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                          <span style={{ fontWeight: 700, color: '#6366f1' }}>{fmt(r.coutPoints)} pts</span>
                          <button className="bt-action-btn bt-action-btn--shop" disabled={busy || !affordable}
                            style={!affordable ? { opacity: .5, cursor: 'not-allowed' } : undefined}
                            onClick={() => handleRedeem(r)}>
                            <Gift size={12}/> Échanger
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Historique */}
              <div className="bt-section-label">Historique</div>
              <div className="bt-preview-table-wrap" style={{ maxHeight: 280, overflow: 'auto' }}>
                <table className="bt-preview-table">
                  <thead><tr><th>Date</th><th>Type</th><th>Motif</th><th>Points</th><th>Solde</th></tr></thead>
                  <tbody>
                    {(detail.transactions || []).map(t => (
                      <tr key={t.id}>
                        <td>{new Date(t.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                        <td>{t.type === 'earn' ? 'Gain' : t.type === 'spend' ? 'Dépense' : 'Ajustement'}</td>
                        <td>{t.reason || '—'}</td>
                        <td style={{ color: t.points >= 0 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                          {t.points >= 0 ? '+' : ''}{fmt(t.points)}
                        </td>
                        <td>{t.balanceApres != null ? fmt(t.balanceApres) : '—'}</td>
                      </tr>
                    ))}
                    {(!detail.transactions || detail.transactions.length === 0) && (
                      <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8' }}>Aucun mouvement</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bt-modal-footer">
              <button type="button" className="bt-btn-cancel" onClick={closeDetail}>Fermer</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
